import { useEffect, useState } from 'react';
import {
  getLastPage,
  handleEnterKeyUp,
  EXPERIMENTS,
  isExperimentEnabled,
} from '@wix/communities-blog-client-common';
import { useSelector } from '../../runtime-context';
import {
  defaultPaginationProps,
  PaginationButtonType,
  MAX_PAGES_TO_HANDLE,
} from '../constants';

type GetButtonEventHandlersFn = (
  pageOnClick: number,
  type: PaginationButtonType,
) =>
  | {
      onClick: React.MouseEventHandler;
      onKeyUp: React.KeyboardEventHandler;
      tabIndex: number;
    }
  | undefined;

type PaginationChangeEvent = {
  page: number;
  from: number;
  to: number;
  buttonType: PaginationButtonType;
};

export type PaginationState = {
  currentPage: number;
  lastPage: number;
  getButtonEventHandlers: GetButtonEventHandlersFn;
};

type UsePaginationStateProps = {
  page?: number;
  pageSize?: number;
  entityCount?: number;
  onChange?: (event: PaginationChangeEvent) => void;
};

export function usePaginationState({
  page = defaultPaginationProps.page,
  entityCount = defaultPaginationProps.entityCount,
  pageSize = defaultPaginationProps.pageSize,
  onChange,
}: UsePaginationStateProps): PaginationState {
  const limitPaginationTo100Pages = useSelector((state) =>
    isExperimentEnabled(state, EXPERIMENTS.LIMIT_PAGINATION_TO_100_PAGES),
  );

  let total = entityCount;
  if (limitPaginationTo100Pages) {
    const entityToIncludeInPagination = pageSize * MAX_PAGES_TO_HANDLE;
    total =
      entityToIncludeInPagination > entityCount
        ? entityCount
        : entityToIncludeInPagination;
  }

  const [state, setState] = useState({
    currentPage: page,
    lastPage: getLastPage(total, pageSize),
  });

  useEffect(() => {
    setState({
      currentPage: page,
      lastPage: getLastPage(total, pageSize),
    });
  }, [page, total, pageSize]);

  const getButtonEventHandlers: GetButtonEventHandlersFn = (
    pageOnClick: number,
    type: PaginationButtonType,
  ) => {
    if (!onChange) {
      return;
    }

    return {
      onClick: handleClick(pageOnClick, type),
      onKeyUp: handleEnterKeyUp(handleClick),
      tabIndex: 0,
    };
  };

  const handleClick = (
    pageOnClick: number,
    buttonType: PaginationButtonType,
  ): React.MouseEventHandler => {
    return (e) => {
      e.preventDefault();

      setState((oldState) => ({ ...oldState, currentPage: pageOnClick }));

      onChange?.({
        page: pageOnClick,
        from: (pageOnClick - 1) * pageSize,
        to: pageOnClick * pageSize,
        buttonType,
      });
    };
  };

  return {
    currentPage: state.currentPage,
    lastPage: state.lastPage,
    getButtonEventHandlers,
  };
}
